import React, { useState, useEffect, useRef } from 'react'
import { useMutation } from 'urql'
import uuidv1 from 'uuid/v1'

import { sendPrefetch } from '../../../../api'
import { NerdTracking } from '~/components/helpers/address-form-tracking'
import { CITY_FIBRE_OUTCODES, MS3_OUTCODES, isProviderOutcode } from '~/components/pages/hull/utils/postcode'
import { usePageContext } from '~/contexts/page-context'
import STORE_CUSTOMER_MUTATION from '~/graphql/mutations/store-customer'
import captureException from '~/utils/capture-exception'
import { setCookie, deleteCookie } from '~/utils/cookies'
import { QLJAddressFormat } from '~/utils/format-address'
import { getProviderNameFromId, getProvidersByPageId, getProvidersByScope } from '~/utils/providers'
import { addressEntryForm, addressEntryBroadbandSpecificFields } from '~/utils/tracking-constants'
import { track, trackSegment } from '~/utils/tracking.js'

import { AddressFinder } from './address-finder'
import styles from './input-form.module.css'
import { ProviderDropdown } from './provider-dropdown'

const ADDRESS_COOKIE_NAME = 'rvu-bb-personalised'
const ADDRESS_COOKIE_VALUE = 'property'
const DAYS_IN_ONE_YEAR = 365

export const InputForm = ({ customerAddress = {}, onUpdate, provider = '', providersOverride }) => {
  const [formId, setFormId] = useState()
  const [address, setAddress] = useState(customerAddress)
  const [currentProviderId, setCurrentProviderId] = useState(provider)
  const [showProviderBox, setShowProviderBox] = useState(!!(customerAddress?.summaryLine || provider))
  const [addressError, setAddressError] = useState(null)
  const [providerError, setProviderError] = useState(null)
  const landingFormRef = useRef(null)
  const [, storeCustomer] = useMutation(STORE_CUSTOMER_MUTATION)

  const { pageId, scope } = usePageContext()

  useEffect(() => {
    setFormId(uuidv1())
  }, [])

  const landingFormProps = addressEntryForm({ formId: 'availability_checker_form', formStartedId: formId })

  const filteredProvidersByPageId = getProvidersByPageId(pageId)
  const providersData = getProvidersByScope(filteredProvidersByPageId, scope)

  const broadbandFormProps = addressEntryBroadbandSpecificFields({
    address,
    providerId: currentProviderId,
    providerName: currentProviderId ? getProviderNameFromId(providersData, currentProviderId) : ''
  })

  const addressChangeHandler = (a) => {
    setAddress(a)

    if (a === null) {
      track('Broadband - Address Capture', 'Clear address')
      deleteCookie(ADDRESS_COOKIE_NAME)
    } else if (!a.summaryLine) {
      track('Broadband - Address Capture', 'Editing address')
    } else if (a.uprn || a.udprn) {
      track('Broadband - Address Capture', 'Address selected')
      sendPrefetch(a)
      setAddressError(null)
    } else {
      track('Broadband - Address Capture', 'Address not listed')
      trackSegment('Element Clicked', {
        element_name: 'Address Not Listed',
        element_text: 'Address Not Listed'
      })
      setAddressError(null)
    }

    if (!showProviderBox) setShowProviderBox(!!a?.summaryLine)
  }

  const providerChangeHandler = (p) => {
    track('Broadband - Provider Capture', p.name)
    setCurrentProviderId(p.id)
    setProviderError(null)
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    const { postcode = '' } = address
    const formValidation = validateForm()
    if (!formValidation.isValid) {
      trackSegment('Form Errored', {
        submit_id: formId,
        form_name: 'landing-form',
        form_started_id: formId,
        form_error_text: formValidation.addressError || formValidation.providerError
      })

      setAddressError(formValidation.addressError)
      setProviderError(formValidation.providerError)

      return
    }

    track('Broadband - Availability Checker', 'Submit')
    await saveAddress()

    setCookie(ADDRESS_COOKIE_NAME, ADDRESS_COOKIE_VALUE, DAYS_IN_ONE_YEAR)

    window.nerdEvents.fireEvent(landingFormRef.current, 'submitted')
    onUpdate(address)

    if (isProviderOutcode(postcode.toUpperCase(), MS3_OUTCODES)) {
      window.location = `/broadband/hull/results`
    } else if (isProviderOutcode(postcode.toUpperCase(), CITY_FIBRE_OUTCODES)) {
      window.location = '/broadband/fibre/results'
    } else {
      window.location = '/broadband/results'
    }
  }

  const validateForm = () => {
    const validity = { isValid: true, addressError: null, providerError: null }

    if (!address?.postcode) {
      track('Broadband - availability checker postcode validation error', 'Please enter a valid postcode')

      validity.isValid = false
      validity.addressError = 'Please enter your postcode e.g. SE1 2LH'
    } else if (!address.summaryLine) {
      track('Broadband - availability checker address validation error', 'Please select an address')

      validity.isValid = false
      validity.addressError = 'Please select an address'
    }

    if (showProviderBox && !currentProviderId) {
      track('Broadband - availability checker provider validation error', 'Please select a provider')

      validity.isValid = false
      validity.providerError = 'Please select your current provider'
    }

    return validity
  }

  const getMutationVariables = (address, formId) => {
    return {
      customer: {
        address: QLJAddressFormat(address),
        formStartedId: formId,
        provider: currentProviderId
      }
    }
  }

  const saveAddress = async () => {
    const result = await storeCustomer(getMutationVariables(address, formId))

    if (result.error) {
      console.error(result.error)
      captureException(result.error)
    }
  }

  const privacyPolicyClickHandler = () =>
    trackSegment('Element Clicked', {
      element_name: 'Privacy notice',
      element_text: 'Privacy notice',
      element_type: 'link'
    })

  return (
    <form
      id='landing-form'
      ref={landingFormRef}
      onSubmit={onSubmit}
      data-nerd
      data-nerd-loaded
      data-tracking-id='landing-form'
    >
      <NerdTracking addressFormProps={landingFormProps} broadbandFormProps={broadbandFormProps} />
      <AddressFinder address={address} onAddressChange={addressChangeHandler} addressError={addressError} />
      {showProviderBox && (
        <ProviderDropdown
          currentProvider={currentProviderId}
          onProviderChange={providerChangeHandler}
          providerError={providerError}
          providersOverride={providersOverride}
        />
      )}
      <button data-cypress-id='input-form-submit' type='submit' className={styles.updateButton}>
        {customerAddress?.summaryLine && provider ? (
          'Update results'
        ) : (
          <>
            Find deals
            <svg className={styles.icon} height={16} width={16}>
              <use xlinkHref='/icons.svg#icon-arrow-right' />
            </svg>
          </>
        )}
      </button>
      <a href='/about-us/privacy-policy' target='_blank' className={styles.link} onClick={privacyPolicyClickHandler}>
        Privacy notice
      </a>
    </form>
  )
}
